(() => {
  const initialize = (scope = document) => {
    const roots = scope.matches?.('[data-puff-cart]') ? [scope] : scope.querySelectorAll('[data-puff-cart]');
    roots.forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      const readConfig = () => JSON.parse(root.querySelector('[data-pfc-config]').textContent);
      let config;
      try { config = readConfig(); } catch { return; }
      root.dataset.initialized = 'true';
      root.classList.add('is-enhanced');
      let busy = false;
      let uncertain = false;
      let pendingCheckout = false;
      let leaving = false;
      let noteDraft = config.note;
      const controller = new AbortController();
      const { signal } = controller;
      root.pfcCleanup = () => controller.abort();
      const status = root.querySelector('[data-pfc-status]');
      const errorBox = root.querySelector('[data-pfc-error]');
      const content = () => root.querySelector('[data-pfc-content]');
      const mainForm = () => root.querySelector('[data-pfc-cart-form]');
      const say = (message) => { if (status) status.textContent = message; };
      const showError = (message) => {
        errorBox.querySelector('[data-pfc-error-text]').textContent = message;
        errorBox.hidden = false;
        say(message);
      };
      const enhance = () => {
        root.querySelectorAll('[data-pfc-quantity-step]').forEach((button) => { button.hidden = false; });
        root.querySelector('[data-pfc-native-update]')?.setAttribute('hidden', '');
        document.querySelectorAll('[data-puff-cart-count]').forEach((node) => {
          node.textContent = config.itemCount;
          node.closest('a')?.setAttribute('aria-label', config.messages.cartCount.replace('%count%', config.itemCount));
        });
      };
      const setBusy = (value) => {
        busy = value;
        root.classList.toggle('is-busy', value);
        root.setAttribute('aria-busy', value ? 'true' : 'false');
        root.querySelectorAll('input, textarea, button').forEach((control) => {
          if (control.matches('[data-pfc-checkout]')) {
            control.setAttribute('aria-disabled', value || uncertain ? 'true' : 'false');
            control.disabled = uncertain;
          } else control.disabled = value || uncertain;
        });
        const label = root.querySelector('[data-pfc-checkout-label]');
        if (label) label.textContent = value ? config.messages.checkoutPending : config.messages.checkout;
      };
      const quantityValue = (input) => {
        if (!input.value.trim()) throw new Error(config.messages.invalidQuantity);
        const value = Number(input.value);
        const step = Number(input.step) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        if (!Number.isInteger(value) || value < 0 || value > max || value % step !== 0) throw new Error(config.messages.invalidQuantity);
        return value;
      };
      const pendingChanges = () => {
        const updates = {};
        root.querySelectorAll('[data-pfc-quantity]').forEach((input) => {
          const value = quantityValue(input);
          if (value !== Number(input.dataset.serverQuantity)) updates[input.closest('[data-pfc-line]').dataset.lineKey] = value;
        });
        return updates;
      };
      const rememberFocus = (target) => {
        const item = target?.closest('[data-pfc-line]');
        return { key: item?.dataset.lineKey, index: Number(item?.dataset.lineIndex || 0), control: target?.dataset.pfcQuantityStep || (target?.matches('[data-pfc-quantity]') ? 'quantity' : ''), noteOpen: !!root.querySelector('[data-pfc-note]')?.open, isNote: !!target?.matches('[data-pfc-note-input]') };
      };
      const restoreFocus = (saved) => {
        const note = root.querySelector('[data-pfc-note]');
        if (note && saved.noteOpen) note.open = true;
        let focusTarget;
        if (saved.isNote) focusTarget = root.querySelector('[data-pfc-note-input]');
        else if (saved.key) {
          const rows = [...root.querySelectorAll('[data-pfc-line]')];
          const row = rows.find((node) => node.dataset.lineKey === saved.key) || rows[Math.min(saved.index, rows.length - 1)];
          if (row) focusTarget = saved.control === 'quantity' ? row.querySelector('[data-pfc-quantity]') : [...row.querySelectorAll('[data-pfc-quantity-step]')].find((node) => node.dataset.pfcQuantityStep === saved.control);
        }
        focusTarget ||= root.querySelector('.pfc__summary-heading h2') || root.querySelector('h1');
        if (focusTarget) {
          if (!focusTarget.hasAttribute('tabindex') && !focusTarget.matches('input, button, textarea, a')) focusTarget.tabIndex = -1;
          focusTarget.focus({ preventScroll: true });
        }
      };
      const applySection = (html, focus) => {
        if (!html || typeof html !== 'string') throw new Error(config.messages.reloadError);
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const incoming = fragment.querySelector('[data-puff-cart] [data-pfc-content]');
        if (!incoming?.querySelector('[data-pfc-config]')) throw new Error(config.messages.reloadError);
        content().replaceChildren(...incoming.childNodes);
        config = readConfig();
        noteDraft = config.note;
        enhance();
        restoreFocus(focus);
      };
      const refresh = async (focus) => {
        const url = new URL(config.cartUrl, window.location.origin);
        url.searchParams.set('sections', config.sectionId);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal });
        if (!response.ok) throw new Error(config.messages.reloadError);
        const sections = await response.json();
        applySection(sections[config.sectionId], focus);
      };
      const post = async (endpoint, payload) => {
        const routeRoot = config.rootUrl.endsWith('/') ? config.rootUrl : `${config.rootUrl}/`;
        const response = await fetch(`${routeRoot}cart/${endpoint}.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...payload, sections: [config.sectionId], sections_url: window.location.pathname }), signal });
        let result;
        try { result = await response.json(); } catch { throw new Error(config.messages.reloadError); }
        if (!response.ok || result.status >= 400) {
          const description = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : config.messages.error;
          throw new Error(description);
        }
        return result;
      };
      const checkout = () => {
        const form = mainForm();
        const button = root.querySelector('[data-pfc-checkout]');
        if (form && button && config.itemCount > 0) form.requestSubmit(button);
      };
      const mutate = async (endpoint, payload, target, successMessage) => {
        if (busy || uncertain || leaving) return false;
        const focus = rememberFocus(target);
        const noteToSave = root.querySelector('[data-pfc-note-input]')?.value ?? noteDraft;
        const noteWasDirty = noteToSave !== config.note;
        errorBox.hidden = true;
        setBusy(true);
        say(endpoint === 'add' ? config.messages.adding : config.messages.updating);
        let succeeded = false;
        try {
          let result = await post(endpoint, payload);
          if (endpoint !== 'update' && noteWasDirty) result = await post('update', { note: noteToSave });
          if (result.sections?.[config.sectionId]) applySection(result.sections[config.sectionId], focus);
          else await refresh(focus);
          succeeded = true;
          say(successMessage || config.messages.updated);
        } catch (error) {
          if (error.name === 'AbortError') return false;
          pendingCheckout = false;
          try { await refresh(focus); } catch { uncertain = true; root.classList.add('is-stale'); }
          if (noteWasDirty) {
            noteDraft = noteToSave;
            const noteInput = root.querySelector('[data-pfc-note-input]');
            if (noteInput) noteInput.value = noteToSave;
          }
          showError(uncertain ? config.messages.reloadError : endpoint === 'add' ? config.messages.addError : config.messages.error);
        } finally { setBusy(false); }
        if (succeeded && pendingCheckout) { pendingCheckout = false; checkout(); }
        return succeeded;
      };
      root.addEventListener('click', (event) => {
        const stepButton = event.target.closest('[data-pfc-quantity-step]');
        const remove = event.target.closest('[data-pfc-remove]');
        if (!stepButton && !remove) return;
        event.preventDefault();
        if (busy || uncertain || leaving) return;
        const row = (stepButton || remove).closest('[data-pfc-line]');
        const input = row.querySelector('[data-pfc-quantity]');
        let quantity = 0;
        try { if (stepButton) quantity = Math.max(0, quantityValue(input) + Number(stepButton.dataset.pfcQuantityStep) * Number(input.step || 1)); }
        catch (error) { showError(error.message); return; }
        input.value = quantity;
        mutate('change', { id: row.dataset.lineKey, quantity }, stepButton || remove);
      }, { signal });
      root.addEventListener('input', (event) => { if (event.target.matches('[data-pfc-note-input]')) noteDraft = event.target.value; }, { signal });
      root.addEventListener('change', (event) => {
        const input = event.target;
        if (input.matches('[data-pfc-addon-option]')) { const price = root.querySelector('[data-pfc-addon-price]'); if (price) price.textContent = input.dataset.formattedPrice; return; }
        if (!input.matches('[data-pfc-quantity]') || busy || uncertain || leaving) return;
        try { const quantity = quantityValue(input); if (quantity !== Number(input.dataset.serverQuantity)) mutate('change', { id: input.closest('[data-pfc-line]').dataset.lineKey, quantity }, input); }
        catch (error) { showError(error.message); }
      }, { signal });
      root.addEventListener('submit', (event) => {
        if (event.target.matches('.pfc__addon-form')) {
          event.preventDefault();
          if (busy || uncertain || leaving) return;
          const selected = event.target.querySelector('[data-pfc-addon-option]:checked');
          if (selected) mutate('add', { items: [{ id: Number(selected.value), quantity: 1 }] }, event.submitter, config.messages.added);
          return;
        }
        if (!event.target.matches('[data-pfc-cart-form]')) return;
        const wantsCheckout = event.submitter?.name === 'checkout';
        if (uncertain || leaving) { event.preventDefault(); return; }
        if (busy) { event.preventDefault(); pendingCheckout ||= wantsCheckout; say(config.messages.checkoutPending); return; }
        try {
          const updates = pendingChanges();
          const note = root.querySelector('[data-pfc-note-input]')?.value ?? noteDraft;
          if (Object.keys(updates).length || note !== config.note) { event.preventDefault(); pendingCheckout = wantsCheckout; mutate('update', { updates, note }, event.submitter); }
          else if (wantsCheckout) { leaving = true; root.setAttribute('aria-busy', 'true'); const label = root.querySelector('[data-pfc-checkout-label]'); if (label) label.textContent = config.messages.checkoutLeaving; say(config.messages.checkoutLeaving); }
        } catch (error) { event.preventDefault(); showError(error.message); }
      }, { signal });
      enhance();
    });
  };
  if (!window.puffCartController) {
    window.puffCartController = { initialize };
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-puff-cart]').forEach((root) => root.pfcCleanup?.()));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
    else initialize();
  } else window.puffCartController.initialize();
})();
